'use client';
import React, { useRef } from 'react';
import { Player, PlayerRef } from '@remotion/player';
import { RemotionComposition } from './RemotionComposition';

// Simple video data interface that matches database structure
interface VideoData {
	title?: string;
	images?: string[];
	audioUrl?: string;
	captionJson?: Array<{
		word: string;
		start: number;
		end: number;
		confidence?: number;
	}>;
	width?: number;
	height?: number;
}

interface RemotionPlayerProps {
	videoData: VideoData;
	size?: 'normal' | 'small';
}

export const RemotionPlayer: React.FC<RemotionPlayerProps> = ({
	videoData,
	size = 'normal',
}) => {
	// Reference to the player for programmatic control
	const playerRef = useRef<PlayerRef>(null);

	// Calculate total duration based on caption data or use default
	const calculateDuration = (): number => {
		if (videoData?.captionJson && videoData.captionJson.length > 0) {
			const lastCaption =
				videoData.captionJson[videoData.captionJson.length - 1];
			return Math.ceil(lastCaption.end * 30) + 30; // Convert seconds to frames at 30fps + 1s buffer
		}
		return 150; // Default 5 seconds at 30fps
	};

	// Get dimensions with fallbacks
	const width = videoData?.width || 720;
	const height = videoData?.height || 1280;
	const durationInFrames = calculateDuration();

	// Container style based on size
	const containerStyle =
		size === 'small'
			? {
					width: '100%',
					height: 'auto',
					maxHeight: '222px',
					borderRadius: '0.75rem',
					overflow: 'hidden',
				}
			: {
					width: '100%',
					height: 'auto',
					minHeight: '333px',
					borderRadius: '0.5rem',
					overflow: 'hidden',
				};

	return (
		<div className='video-player-container' style={containerStyle}>
			<Player
				ref={playerRef}
				component={RemotionComposition}
				durationInFrames={durationInFrames}
				compositionWidth={width}
				compositionHeight={height}
				fps={30}
				controls
				loop
				showVolumeControls
				autoPlay
				style={{
					width: '100%',
					maxWidth: '100%',
					aspectRatio: `${width}/${height}`,
					borderRadius: '0.5rem',
					overflow: 'hidden',
				}}
				inputProps={{
					title: videoData.title,
					images: videoData.images,
					audioUrl: videoData.audioUrl,
					captionJson: videoData.captionJson || [],
				}}
			/>
		</div>
	);
};

export default RemotionPlayer;
